/**
 * GlobeScene — the ambient hero backdrop.
 *
 * A slowly rotating wireframe globe ringed by an orbital band, overlaid with
 * live "trajectory" arcs between sites on the surface. Bright pulses travel
 * each arc — amber running forward, cyan running inverted (a nod to TENET's
 * opposed entropy). Reads as a global mission / recon picture: Palantir-map
 * meets Anduril ops console. Purely decorative; pointer events stay off.
 */

import { useMemo, useRef, type ReactNode } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Line, Stars } from "@react-three/drei";
import * as THREE from "three";

const STEEL = new THREE.Color("#39455c");
const AMBER = new THREE.Color("#ff7c30");
const CYAN = new THREE.Color("#3fc8f0");
const R = 2;

/** Uniform-ish random point on a sphere of radius r. */
function pointOnSphere(r: number): THREE.Vector3 {
  const u = Math.random();
  const v = Math.random();
  const theta = 2 * Math.PI * u;
  const phi = Math.acos(2 * v - 1);
  return new THREE.Vector3(
    r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}

interface Arc {
  curve: THREE.QuadraticBezierCurve3;
  points: [number, number, number][];
  color: THREE.Color;
  speed: number;
  offset: number;
}

/** The dim globe body + wireframe meridians + a tilted orbital ring. */
function Globe() {
  return (
    <group>
      {/* Opaque core occludes far-side arcs so the globe reads as solid */}
      <mesh>
        <sphereGeometry args={[R * 0.985, 48, 32]} />
        <meshBasicMaterial color="#070b14" />
      </mesh>
      {/* Wireframe lat/long shell */}
      <mesh>
        <sphereGeometry args={[R, 36, 24]} />
        <meshBasicMaterial color={STEEL} wireframe transparent opacity={0.22} />
      </mesh>
      {/* Bright equator */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[R, 0.004, 8, 96]} />
        <meshBasicMaterial color={CYAN} transparent opacity={0.25} />
      </mesh>
      {/* Tilted orbital ring */}
      <mesh rotation={[Math.PI / 2.4, 0.3, 0]}>
        <torusGeometry args={[R * 1.42, 0.006, 8, 128]} />
        <meshBasicMaterial color={AMBER} transparent opacity={0.35} />
      </mesh>
    </group>
  );
}

/** Trajectory arcs between surface sites, with a pulse traveling each. */
function Trajectories({ count = 16 }: { count?: number }) {
  const pulses = useRef<THREE.InstancedMesh>(null);
  const sites = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const arcs = useMemo<Arc[]>(() => {
    return Array.from({ length: count }, (_, i) => {
      const a = pointOnSphere(R);
      const b = pointOnSphere(R);
      // Lift the control point off the surface to bow the arc outward; longer
      // hops bow higher, like a ballistic / orbital trajectory.
      const mid = a.clone().add(b).multiplyScalar(0.5);
      mid.normalize().multiplyScalar(R * (1.18 + a.distanceTo(b) * 0.18));
      const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
      const points = curve.getPoints(50).map((p) => [p.x, p.y, p.z] as [number, number, number]);
      return {
        curve,
        points,
        color: i % 3 === 0 ? CYAN : AMBER,
        speed: 0.06 + Math.random() * 0.12,
        offset: Math.random(),
      };
    });
  }, [count]);

  // Endpoint site markers (static dots at every arc end).
  const endpoints = useMemo(
    () => arcs.flatMap((arc) => [arc.curve.v0, arc.curve.v2]),
    [arcs],
  );

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (pulses.current) {
      arcs.forEach((arc, i) => {
        const u = (t * arc.speed + arc.offset) % 1;
        const p = arc.curve.getPoint(u);
        // Fade the pulse in/out at the ends so it doesn't pop.
        const fade = Math.sin(u * Math.PI);
        dummy.position.copy(p);
        dummy.scale.setScalar(0.018 + fade * 0.05);
        dummy.updateMatrix();
        pulses.current!.setMatrixAt(i, dummy.matrix);
        pulses.current!.setColorAt(i, arc.color);
      });
      pulses.current.instanceMatrix.needsUpdate = true;
      if (pulses.current.instanceColor) pulses.current.instanceColor.needsUpdate = true;
    }
    if (sites.current) {
      endpoints.forEach((p, i) => {
        dummy.position.copy(p);
        dummy.scale.setScalar(0.022);
        dummy.updateMatrix();
        sites.current!.setMatrixAt(i, dummy.matrix);
      });
      sites.current.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <group>
      {arcs.map((arc, i) => (
        <Line
          key={i}
          points={arc.points}
          color={arc.color === CYAN ? "#3fc8f0" : "#ff7c30"}
          lineWidth={1}
          transparent
          opacity={0.28}
          toneMapped={false}
        />
      ))}
      <instancedMesh ref={pulses} args={[undefined, undefined, count]}>
        <sphereGeometry args={[1, 10, 10]} />
        <meshBasicMaterial toneMapped={false} />
      </instancedMesh>
      <instancedMesh ref={sites} args={[undefined, undefined, endpoints.length]}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#ffb27a" toneMapped={false} transparent opacity={0.6} />
      </instancedMesh>
    </group>
  );
}

/** Whole-system slow spin + gentle camera drift. */
function Rig({ children }: { children: ReactNode }) {
  const group = useRef<THREE.Group>(null);
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (group.current) group.current.rotation.y = t * 0.045;
    state.camera.position.x = Math.sin(t * 0.08) * 0.5;
    state.camera.position.y = 0.4 + Math.cos(t * 0.06) * 0.3;
    state.camera.lookAt(0, 0, 0);
  });
  return <group ref={group}>{children}</group>;
}

export function GlobeScene({ className }: { className?: string }) {
  return (
    <Canvas
      className={className}
      camera={{ position: [0, 0.4, 6], fov: 46 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
    >
      <Rig>
        {/* Tilt the planet's axis for a more dynamic read */}
        <group rotation={[0.32, 0, 0.12]}>
          <Globe />
          <Trajectories count={16} />
        </group>
      </Rig>
      <Stars radius={80} depth={50} count={1800} factor={3} saturation={0} fade speed={0.3} />
    </Canvas>
  );
}
